import React, { useState, useEffect } from 'react';
import './TemplateComponents.css';

/**
 * TemplateSelector Component
 * 
 * Shows available templates for applying to a spec sheet
 * Used in MARKERMAKER Step 3 when importing spec
 * 
 * Usage:
 * <TemplateSelector 
 *   specData={specData}
 *   onSelectTemplate={handleSelect}
 *   onSkip={handleSkip}
 * />
 */

const TemplateSelector = ({ specData, onSelectTemplate, onSkip, garmentType = null }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showMappingPreview, setShowMappingPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch templates from API
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Filter templates by garment type if provided
  useEffect(() => {
    if (garmentType) {
      setFilteredTemplates(templates.filter(t => t.garment_type === garmentType));
    } else {
      setFilteredTemplates(templates);
    }
  }, [templates, garmentType]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/pom-templates-api.php?action=list');
      const data = await response.json();

      if (data.success) {
        setTemplates(data.templates);
        setError(null);
      } else {
        setError(data.error || 'Failed to load templates');
      }
    } catch (err) {
      setError('Error connecting to API: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) {
      alert('Please select a template');
      return;
    }

    if (onSelectTemplate) {
      onSelectTemplate(selectedTemplate);
    }
  };

  const getMappingPreview = (template) => {
    const config = JSON.parse(template.mapping_config || '{}');
    const measurements = config.measurements || [];
    
    return (
      <div className="mapping-preview">
        <h5>Measurement Mappings:</h5>
        <ul>
          {measurements.map((m, idx) => (
            <li key={idx}>
              <code>{m.pattern_key}</code> → Column <code>{m.spec_column}</code>
              {m.spec_label && <span> ({m.spec_label})</span>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="template-selector loading">
        <p>Loading available templates...</p>
      </div>
    );
  }

  return (
    <div className="template-selector">
      <div className="selector-header">
        <h3>🔗 Select Template for Measurement Mapping</h3>
        <p>
          Choose a template to automatically map your spec sheet measurements 
          to pattern measurements. This enables auto-grading!
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <strong>⚠️ Error:</strong> {error}
          <button onClick={fetchTemplates} className="btn-small">Retry</button>
        </div>
      )}

      {filteredTemplates.length === 0 ? (
        <div className="no-templates-message">
          <p>📋 No templates available for this garment type.</p>
          <p>You can create a custom template or proceed without mapping.</p>
        </div>
      ) : (
        <div className="templates-list">
          {filteredTemplates.map(template => {
            const config = JSON.parse(template.mapping_config || '{}');
            const measurementCount = (config.measurements || []).length;
            const isSelected = selectedTemplate?.id === template.id;
            const isStandard = template.is_standard === 1;

            return (
              <div 
                key={template.id} 
                className={`template-option ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="option-header">
                  <div className="option-checkbox">
                    <input
                      type="radio"
                      name="template"
                      value={template.id}
                      checked={isSelected}
                      onChange={() => setSelectedTemplate(template)}
                    />
                  </div>

                  <div className="option-info">
                    <h4>{template.name}</h4>
                    <p className="description">{template.description}</p>
                  </div>

                  <div className="option-badges">
                    {isStandard && <span className="badge standard">Standard</span>}
                    <span className="badge measurements">{measurementCount} mappings</span>
                  </div>

                  {isSelected && (
                    <button 
                      className="btn-preview"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMappingPreview(!showMappingPreview);
                      }}
                    >
                      👁️ {showMappingPreview ? 'Hide' : 'Show'} Mappings
                    </button>
                  )}
                </div>

                {isSelected && showMappingPreview && getMappingPreview(template)}
              </div>
            );
          })}
        </div>
      )}

      <div className="selector-actions">
        <button 
          className="btn-primary"
          onClick={handleApplyTemplate}
          disabled={!selectedTemplate}
        >
          ✓ Apply Template & Continue
        </button>

        <button 
          className="btn-secondary"
          onClick={onSkip}
        >
          ⊘ Skip Template (Manual Mapping)
        </button>
      </div>

      {selectedTemplate && (
        <div className="selection-summary">
          <strong>Selected:</strong> {selectedTemplate.name}
          <br />
          <small>
            Will map {JSON.parse(selectedTemplate.mapping_config || '{}').measurements?.length || 0} measurements 
            from your spec sheet to the pattern.
          </small>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
